import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Check, Upload, Palette, Package, Loader2, Pencil, Lock, Link, Store, Layers, X, RotateCcw } from 'lucide-react';
import ImageUploader from './ImageUploader';
import ImageEditor from './ImageEditor';
import ProductSelector from './ProductSelector';
import UpgradeLimitModal from './UpgradeLimitModal';
import CreationWaitScreen from './CreationWaitScreen';
import { amplifyApi } from '../services/amplifyApi';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import awsExports from '../aws-exports';

const STEPS = [
  { id: 'upload', title: 'Upload', icon: Upload },
  { id: 'edit', title: 'Edit', icon: Pencil },
  { id: 'approve', title: 'Approve', icon: Check },
  { id: 'select', title: 'Products', icon: Package },
  { id: 'create', title: 'Creating', icon: Loader2 }
];

const DesignWorkflow = ({ onComplete, onError, initialDesign, preSelectedBlueprint, onClearPreSelection }) => {
  const { requireAuth, isAuthenticated } = useAuth();
  const brand = useBrand();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState('');
  const [completedProducts, setCompletedProducts] = useState(0);
  const [userLimits, setUserLimits] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [browsedBlueprint, setBrowsedBlueprint] = useState(null);

  // Effective pre-selection: from "Create Similar" (prop) OR "Browse Products" tab (internal state)
  const effectivePreSelection = preSelectedBlueprint || browsedBlueprint;

  // Load initial design if provided (from saved designs)
  useEffect(() => {
    if (initialDesign) {
      setUploadedImage({
        id: initialDesign.id,
        url: initialDesign.url,
        name: initialDesign.name,
        fromSaved: true,
        // Preserve s3Key for direct S3 access (bypasses CORS issues)
        s3Key: initialDesign.s3Key || null,
        // Preserve printifyImageId if the saved design has it
        printifyImageId: initialDesign.printifyImageId || null
      });
      // Start at edit step so user can adjust if needed
      setCurrentStep(1);
    }
  }, [initialDesign]);

  // Handle pre-selected blueprint (from "Create Similar" button or Browse Products tab)
  useEffect(() => {
    if (preSelectedBlueprint?.blueprintId) {
      setSelectedCategories([String(preSelectedBlueprint.blueprintId)]);
    }
  }, [preSelectedBlueprint]);

  // Sync browse selection with selectedCategories
  useEffect(() => {
    if (browsedBlueprint?.selectedIds?.length > 0) {
      setSelectedCategories(browsedBlueprint.selectedIds);
    }
  }, [browsedBlueprint]);

  // Fetch categories when component mounts or when reaching selection step
  useEffect(() => {
    if (currentStep === 3 && categories.length === 0) {
      fetchCategories();
    }
  }, [currentStep]);

  // Fetch user limits when authenticated and reaching selection step
  useEffect(() => {
    const fetchLimits = async () => {
      if (isAuthenticated && brand?.features?.limits?.enabled !== false) {
        try {
          const response = await amplifyApi.getUserLimits();
          if (response.success) {
            setUserLimits(response);
          }
        } catch (err) {
          console.error('Error fetching user limits:', err);
        }
      }
    };

    if (currentStep === 3) {
      fetchLimits();
    }
  }, [currentStep, isAuthenticated, brand?.features?.limits?.enabled]);

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await amplifyApi.getProductCategories();
      if (response.success) {
        if (response.categories) {
          // Sort categories alphabetically by name
          const sortedCategories = [...response.categories].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          );
          setCategories(sortedCategories);
        }
        if (response.blueprints) {
          setBlueprints(response.blueprints);
        }

        // Load defaults from localStorage if no selection yet
        // BUT only if we don't have a pre-selected blueprint from "Create Similar" or Browse Products
        const savedDefaults = localStorage.getItem('merchpie_default_categories');
        if (savedDefaults && selectedCategories.length === 0 && !effectivePreSelection) {
          try {
            const defaults = JSON.parse(savedDefaults);
            // Filter to only include valid blueprint IDs
            const validDefaults = defaults.filter(id =>
              response.blueprints?.some(bp => String(bp.id) === String(id))
            );
            if (validDefaults.length > 0) {
              setSelectedCategories(validDefaults);
            }
          } catch (e) {
            console.error('Failed to parse saved defaults:', e);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      onError?.('Failed to load product categories. Please try again.');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleImageUpload = (imageData) => {
    setUploadedImage(imageData);
    // Auto-advance to approval step
    setCurrentStep(1);
  };

  const handleEditSave = (editedImage) => {
    console.log('handleEditSave received:', {
      edited: editedImage?.edited,
      printifyImageId: editedImage?.printifyImageId,
      urlType: editedImage?.url?.startsWith('data:') ? 'dataUrl' : 'externalUrl',
      urlLength: editedImage?.url?.length
    });
    setUploadedImage(editedImage);
    setCurrentStep(2); // Move to approve step
  };

  const handleRemoveBackground = async (imageData, retryCount = 0) => {
    const maxRetries = 2; // Will try up to 3 times total

    try {
      const response = await amplifyApi.removeBackground(imageData);

      // Handle auth/rate limit errors - pass them through to ImageEditor
      if (response.error === 'AUTH_REQUIRED' || response.error === 'RATE_LIMITED') {
        return { error: response.error, message: response.message };
      }

      if (response.success && response.image) {
        return response.image;
      }
      throw new Error(response.error || 'Failed to remove background');
    } catch (error) {
      console.error('Background removal error:', error);

      // Check for auth/rate limit errors that came as exceptions
      const errorData = error.response?.data || {};
      if (errorData.error === 'AUTH_REQUIRED' || errorData.error === 'RATE_LIMITED') {
        return { error: errorData.error, message: errorData.message };
      }

      // Check if this is a retry-able error (service busy/timeout)
      const isRetryable = error.message?.includes('RETRY_SUGGESTED') ||
                          error.message?.includes('busy') ||
                          error.message?.includes('timeout') ||
                          error.message?.includes('taking longer');

      if (isRetryable && retryCount < maxRetries) {
        console.log(`Retrying background removal (attempt ${retryCount + 2} of ${maxRetries + 1})...`);
        // Wait a bit before retrying (exponential backoff: 2s, 4s)
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
        return handleRemoveBackground(imageData, retryCount + 1);
      }

      // Clean up the error message for display
      let errorMessage = error.message || 'Failed to remove background. Please try again.';
      errorMessage = errorMessage.replace('RETRY_SUGGESTED: ', '');

      onError?.(errorMessage);
      return null;
    }
  };

  const handleApprove = () => {
    // Prefetch categories before moving to selection
    fetchCategories();
    setCurrentStep(3);
  };

  const doCreateProducts = async () => {
    // Double-check to prevent race conditions
    if (isCreating) {
      console.log('doCreateProducts already in progress, ignoring');
      return;
    }

    setCurrentStep(4);
    setIsCreating(true);
    setCompletedProducts(0);
    setCreationProgress('Initializing product creation...');

    console.log('doCreateProducts starting with uploadedImage:', {
      edited: uploadedImage?.edited,
      printifyImageId: uploadedImage?.printifyImageId,
      fromSaved: uploadedImage?.fromSaved,
      urlType: uploadedImage?.url?.startsWith('data:') ? 'dataUrl' : 'externalUrl',
      urlLength: uploadedImage?.url?.length
    });

    try {
      let imageToUse = uploadedImage;

      // For saved designs, always force re-upload to Printify to ensure we use the current image
      // This handles cases where:
      // 1. Image was edited (AI edit, bg removed, etc.) but old printifyImageId was saved before the fix
      // 2. The saved design went through ImageEditor which exports to data URL
      // 3. The S3 image is the authoritative source, not the cached printifyImageId
      const imageUrl = uploadedImage.url;
      const shouldForceReupload = uploadedImage.fromSaved === true;

      if (shouldForceReupload) {
        console.log('Saved design detected (fromSaved=true), forcing re-upload to Printify to ensure current image is used');
      }

      // If image doesn't have a printifyImageId OR is from our S3 saved designs, upload to Printify
      if (!uploadedImage.printifyImageId || shouldForceReupload) {
        setCreationProgress('Uploading design to Printify...');
        console.log('Checking image URL type:', imageUrl?.substring(0, 50));
        if (imageUrl && imageUrl.startsWith('data:')) {
          // Upload the data URL directly
          console.log('Uploading edited data URL to Printify, length:', imageUrl.length);
          const uploadResponse = await amplifyApi.uploadDataUrl(
            imageUrl,
            uploadedImage.name || 'saved-design.png'
          );

          console.log('Upload response:', {
            success: uploadResponse.success,
            printifyImageId: uploadResponse.image?.printifyImageId,
            error: uploadResponse.error
          });

          if (uploadResponse.success && uploadResponse.image) {
            // Update the image with the Printify image ID
            imageToUse = {
              ...uploadedImage,
              ...uploadResponse.image,
              printifyImageId: uploadResponse.image.printifyImageId
            };
            console.log('Updated imageToUse with new printifyImageId:', imageToUse.printifyImageId);
            setUploadedImage(imageToUse);
          } else {
            throw new Error(uploadResponse.error || 'Failed to upload image to Printify');
          }
        } else if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
          // For external URLs, we need to fetch and convert to data URL first
          setCreationProgress('Preparing design for upload...');
          try {
            // Check if this is an S3 URL that needs proxying (Printify or our own bucket)
            const needsProxy = imageUrl.includes('pfy-prod-image-storage.s3') ||
                               imageUrl.includes('images-api.printify') ||
                               imageUrl.includes('printifyapp') ||
                               imageUrl.includes('amazonaws.com');

            let fetchUrl = imageUrl;
            if (needsProxy) {
              // Use our backend proxy to fetch Printify images (avoids CORS)
              // Get API endpoint from aws-exports for correct environment (dev vs wiprod)
              const apiEndpoint = awsExports.aws_cloud_logic_custom?.[0]?.endpoint || 'https://53ogiynuu2.execute-api.us-east-1.amazonaws.com/dev';
              fetchUrl = `${apiEndpoint}/api/images/proxy?url=${encodeURIComponent(imageUrl)}`;
            }

            const response = await fetch(fetchUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.status}`);
            }
            const blob = await response.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

            const uploadResponse = await amplifyApi.uploadDataUrl(
              dataUrl,
              uploadedImage.name || 'saved-design.png'
            );

            if (uploadResponse.success && uploadResponse.image) {
              imageToUse = {
                ...uploadedImage,
                ...uploadResponse.image,
                printifyImageId: uploadResponse.image.printifyImageId
              };
              setUploadedImage(imageToUse);
            } else {
              throw new Error(uploadResponse.error || 'Failed to upload image to Printify');
            }
          } catch (fetchError) {
            console.error('Failed to fetch and upload image:', fetchError);
            throw new Error('Failed to prepare image for product creation. Please try uploading a new image.');
          }
        } else {
          throw new Error('Invalid image format. Please upload a new image.');
        }
      }

      // Create products one at a time to avoid API Gateway timeout
      // This allows users to see products as they're created
      const totalProducts = selectedCategories.length;
      const createdProducts = [];
      const failedProducts = [];

      for (let i = 0; i < totalProducts; i++) {
        const category = selectedCategories[i];
        setCreationProgress(`Creating product ${i + 1} of ${totalProducts}...`);

        try {
          const response = await amplifyApi.createProducts(
            imageToUse.id,
            imageToUse,
            [category] // Pass single category as array
          );

          if (response.success && response.products && response.products.length > 0) {
            createdProducts.push(...response.products);
            setCompletedProducts(createdProducts.length);
            setCreationProgress(`Created ${createdProducts.length} of ${totalProducts} products`);
          } else if (response.error === 'PRODUCT_LIMIT_EXCEEDED') {
            // Handle product limit exceeded - show upgrade modal
            setLimitError(response);
            setShowUpgradeModal(true);
            setCurrentStep(3); // Go back to selection
            setIsCreating(false);
            return; // Stop creating more products
          } else {
            console.warn(`Failed to create product for category ${category}:`, response.message);
            failedProducts.push({ category, error: response.message || 'Unknown error' });
          }
        } catch (productError) {
          console.error(`Error creating product for category ${category}:`, productError);
          // Check if error response contains limit exceeded
          if (productError.response?.data?.error === 'PRODUCT_LIMIT_EXCEEDED') {
            setLimitError(productError.response.data);
            setShowUpgradeModal(true);
            setCurrentStep(3);
            setIsCreating(false);
            return;
          }
          failedProducts.push({ category, error: productError.message });
        }
      }

      // Report results
      if (createdProducts.length > 0) {
        if (failedProducts.length > 0) {
          setCreationProgress(`Created ${createdProducts.length} products (${failedProducts.length} failed)`);
        } else {
          setCreationProgress('All products created successfully!');
        }
        // Small delay to show success message
        await new Promise(resolve => setTimeout(resolve, 500));
        onComplete?.(createdProducts);
      } else {
        throw new Error('No products were created. Please try again.');
      }
    } catch (error) {
      console.error('Product creation error:', error);
      onError?.(error.message || 'Failed to create products. Please try again.');
      // Go back to selection step on error
      setCurrentStep(3);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateProducts = () => {
    // Prevent duplicate submissions
    if (isCreating) {
      console.log('Already creating products, ignoring duplicate request');
      return;
    }

    if (selectedCategories.length === 0) {
      onError?.('Please select at least one product category');
      return;
    }

    // Require authentication to create products
    requireAuth('Sign in to create your products', doCreateProducts);
  };

  const goBack = () => {
    if (currentStep > 0 && currentStep < 4) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStartOver = () => {
    // Reset all state to initial values
    setUploadedImage(null);
    setCurrentStep(0);
    setSelectedCategories([]);
    setBrowsedBlueprint(null);
    // Clear pre-selected blueprint if any
    if (onClearPreSelection) {
      onClearPreSelection();
    }
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 0: return !!uploadedImage;
      case 1: return !!uploadedImage; // Edit step - can skip
      case 2: return !!uploadedImage; // Approve step
      case 3: return selectedCategories.length > 0; // Select step
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Skip edit, go to approve
      setCurrentStep(2);
    } else if (currentStep === 2) {
      handleApprove();
    } else if (currentStep === 3) {
      handleCreateProducts();
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;
        const isCreatingStep = index === 3;

        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${isActive
                    ? 'text-white shadow-lg'
                    : isCompleted
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-100 text-slate-400'
                  }
                  ${isCreatingStep && isActive ? 'animate-pulse' : ''}
                `}
                style={isActive ? {
                  backgroundColor: brand.colors.primary,
                  boxShadow: `0 10px 15px -3px ${brand.colors.primary}40`
                } : {}}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className={`w-5 h-5 ${isActive && isCreatingStep ? 'animate-spin' : ''}`} />
                )}
              </div>
              <span
                className={`text-xs mt-2 font-medium hidden sm:block
                  ${isCompleted ? 'text-green-600' : !isActive ? 'text-slate-400' : ''}
                `}
                style={isActive ? { color: brand.colors.primary } : {}}
              >
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-12 sm:w-20 h-0.5 mx-2 transition-colors duration-300
                ${index < currentStep ? 'bg-green-500' : 'bg-slate-200'}
              `} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Context banner for pre-selected products ("Create Similar" or Browse Products) */}
            {effectivePreSelection && effectivePreSelection.productTitle && (
              <div
                className="rounded-xl p-4 border"
                style={{
                  background: `linear-gradient(to right, ${brand.colors.primary}08, ${brand.colors.primary}12)`,
                  borderColor: `${brand.colors.primary}30`
                }}
              >
                <div className="flex items-center gap-4">
                  {/* Product thumbnail */}
                  {effectivePreSelection.mockupUrl && (
                    <div
                      className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden shadow-sm border"
                      style={{ borderColor: `${brand.colors.primary}30` }}
                    >
                      <img
                        src={effectivePreSelection.mockupUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Layers className="w-4 h-4" style={{ color: brand.colors.primary }} />
                      <span className="text-sm font-semibold" style={{ color: brand.colors.primaryDark || brand.colors.primary }}>
                        {preSelectedBlueprint ? 'Creating Similar Product' : `Selected Product${(effectivePreSelection.count || 0) > 1 ? 's' : ''}`}
                      </span>
                    </div>
                    <p className="font-medium truncate" style={{ color: brand.colors.primary }}>
                      {effectivePreSelection.count > 1
                        ? `${effectivePreSelection.count} products selected`
                        : effectivePreSelection.productTitle?.replace(' - Custom Design', '')}
                    </p>
                    <p className="text-sm mt-0.5" style={{ color: `${brand.colors.primary}cc` }}>
                      Upload your own design below
                    </p>
                  </div>
                  {/* Cancel button */}
                  <button
                    onClick={() => {
                      setBrowsedBlueprint(null);
                      setSelectedCategories([]);
                      if (onClearPreSelection) onClearPreSelection();
                    }}
                    className="flex-shrink-0 p-2 rounded-lg transition-colors"
                    style={{ color: brand.colors.primary }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${brand.colors.primary}15`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                    title="Clear selection"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900">
                {brand.features?.hero?.heading || 'Start Creating'}
              </h2>
              <p className="text-slate-500 mt-2">
                {brand.features?.hero?.subtitle || 'Upload your artwork to get started, or browse products first to see what you can create.'}
              </p>
            </div>
            <ImageUploader
              onImageUpload={handleImageUpload}
              onBrowseProductSelect={(info) => {
                if (info.selectedIds.length === 0) {
                  setBrowsedBlueprint(null);
                  setSelectedCategories([]);
                } else {
                  setBrowsedBlueprint(info);
                }
              }}
            />
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900">Edit Your Design</h2>
              <p className="text-slate-500 mt-2">Adjust your image or skip to continue</p>
            </div>

            {uploadedImage && (
              <ImageEditor
                image={uploadedImage}
                onSave={handleEditSave}
                onCancel={() => setCurrentStep(2)} // Skip to approve
                onRemoveBackground={handleRemoveBackground}
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-900">Approve Your Design</h2>
              <p className="text-slate-500 mt-2">Review your design before creating products</p>
            </div>

            {uploadedImage && (
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 max-w-md">
                  <img
                    src={uploadedImage.url}
                    alt="Your design"
                    className="w-full h-auto max-h-80 object-contain rounded-xl"
                  />
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Edit Design
                  </button>
                  <span className="text-slate-300">|</span>
                  <p className="text-sm text-slate-500">
                    This design will be applied to your selected products
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Header with design preview */}
            <div className="flex items-center gap-6">
              {/* Design thumbnail */}
              {uploadedImage?.url && (
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-50 shadow-sm">
                    <img
                      src={uploadedImage.url}
                      alt="Your design"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
              <div className={uploadedImage?.url ? 'text-left' : 'text-center w-full'}>
                <h2 className="text-2xl font-bold text-slate-900">Select Products</h2>
                <p className="text-slate-500 mt-1">Choose which products to create with your design</p>
              </div>
            </div>

            {/* Pre-selected product info banner */}
            {effectivePreSelection && effectivePreSelection.productTitle && (
              <div
                className="rounded-xl p-4 border"
                style={{ backgroundColor: `${brand.colors.primary}08`, borderColor: `${brand.colors.primary}25` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${brand.colors.primary}20` }}
                  >
                    <Layers className="w-4 h-4" style={{ color: brand.colors.primary }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm" style={{ color: brand.colors.primaryDark || brand.colors.primary }}>
                      <strong>{preSelectedBlueprint ? 'Creating similar to:' : 'Pre-selected from browse:'}</strong>{' '}
                      {effectivePreSelection.count > 1
                        ? `${effectivePreSelection.count} products`
                        : effectivePreSelection.productTitle}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: `${brand.colors.primary}bb` }}>
                      {preSelectedBlueprint
                        ? 'The same product type is pre-selected below. You can change it if you prefer a different product.'
                        : 'Your browsed selections are pre-selected below. You can add or remove products.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy info banner */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-800 text-sm">Your products are private by default</h4>
                  <p className="text-slate-600 text-sm mt-1">
                    Products you create are hidden from public view. You can:
                  </p>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Link className="w-3.5 h-3.5" />
                      <span>Sell directly via shareable links</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Store className="w-3.5 h-3.5" />
                      <span>List on marketplace when ready</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ProductSelector
              categories={categories}
              blueprints={blueprints}
              selectedCategories={selectedCategories}
              onSelectionChange={setSelectedCategories}
              isLoading={isLoadingCategories}
              limits={userLimits}
              onUpgradeClick={() => {
                setShowUpgradeModal(true);
              }}
            />
          </div>
        );

      case 4:
        return (
          <CreationWaitScreen
            creationProgress={creationProgress}
            totalProducts={selectedCategories.length}
            completedProducts={completedProducts}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
      {renderStepIndicator()}

      <div>
        {renderCurrentStep()}
      </div>

      {/* Navigation buttons */}
      {currentStep < 4 && currentStep !== 1 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <button
              onClick={goBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                ${currentStep === 0
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }
              `}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {/* Start Over button - show when we have an image and are past upload step */}
            {uploadedImage && currentStep > 0 && (
              <button
                onClick={handleStartOver}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Start over with a new image"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Start Over
              </button>
            )}
          </div>

          {currentStep > 0 && (
            <button
              onClick={handleNext}
              disabled={!canGoNext()}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all
                ${canGoNext()
                  ? 'text-white shadow-sm hover:shadow'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }
              `}
              style={canGoNext() ? {
                backgroundColor: brand.colors.primary,
              } : {}}
              onMouseEnter={(e) => canGoNext() && (e.target.style.backgroundColor = brand.colors.primaryHover)}
              onMouseLeave={(e) => canGoNext() && (e.target.style.backgroundColor = brand.colors.primary)}
            >
              {currentStep === 3 ? (
                <>
                  Create {selectedCategories.length} Product{selectedCategories.length !== 1 ? 's' : ''}
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Start Over button for Edit step (step 1) which has its own navigation */}
      {currentStep === 1 && uploadedImage && (
        <div className="flex justify-center mt-6 pt-4 border-t border-slate-200">
          <button
            onClick={handleStartOver}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Start over with a new image"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Start Over
          </button>
        </div>
      )}

      {/* Upgrade modal for product limits */}
      <UpgradeLimitModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setLimitError(null);
        }}
        limits={userLimits}
        limitError={limitError}
      />
    </div>
  );
};

export default DesignWorkflow;
